// Sentence text (studio/ART-BIBLE.md, "Sentence text"). Everything read as a sentence is drawn here, not in the
// 640x360 pixel raster: sharp, at screen resolution, in one readable font, on a canvas laid over the scene.
// The browser draws each line, then it is thresholded to ink or paper, so the screen keeps its two colours.
// Sizes come from the stylesheet (onebit.css): the element named in ROLES carries each role's font, and its px
// size is the size on screen at the 2x desktop scale (1280x720); at 3x and 4x the text grows with the pixels.

import { ruleFitPx } from './first-minute.js';

export const SENTENCE_FONT = 'Atkinson Hyperlegible';
export const MIN_PX = 14;   // the art bible's floor, on screen at 2x
export const REF_SCALE = 2; // stylesheet px are screen px at this integer scale
// Sprint 22 (readable text at fractional scaling and zoom): a pixel is ink from about 25% coverage, not 50% (or 41%
// before), so the thin strokes of small Atkinson text (the l of 'wallet', the i of 'Night') survive 125% and 150% display
// scaling and 90% or 110% zoom. The CSS one-bit filters (#onebit in index.html, #bit in overview, roadmap and verify) cut
// at the same coverage; test/readable-zoom.test.js holds them to it.
// Bold strokes are two pixels and more, so they never dropped out; at 25% their small counters (a, e, g, s) fill in,
// so bold text keeps the 50% cut (#bitb in the pages; a little leaner on the canvas, which cut at 41%).
export const INK_FROM = 0.25;                            // coverage from which a pixel of regular text is ink
export const CUT = Math.round(255 * (1 - INK_FROM));     // 191: a pixel darker than this (0..255) is ink
export const INK_FROM_BOLD = 0.5;                        // the same for bold text (600 and up)
export const CUT_BOLD = Math.round(255 * (1 - INK_FROM_BOLD)); // 128
export const cutFor = weight => (Number(weight) >= 600 || weight === 'bold' ? CUT_BOLD : CUT);
// the smallest text drawn through a one-bit threshold, CSS px at 2x (the disclaimer strip keeps its pinned 14 px)
export const SMALL_PX = 15;

// role -> the page element whose computed font the role uses
export const ROLES = { chat: '#chat', rule: '#note', board: '#board', receipt: '#receipt', verdict: '#rs', print: '.foot', coach: '#coach',
  // sprint 12 (roadmap 23): titles, the menu bar, small lines, buttons and labels, tags, the knock stamp, verdict points
  title: '#t-title', menu: '#t-menu', line: '#t-line', label: '#t-label', tag: '#t-tag', stamp: '#t-stamp', points: '#t-points' };
// the floor for each kind of text, on screen at 2x (studio/ART-BIBLE.md): titles and the menu bar 16 px, the rest 14
export const MIN_TITLE_PX = 16;
export const BOLD_ROLES = ['title', 'label', 'tag', 'stamp', 'points'];

export const screenPx = (css, scale) => css * scale / REF_SCALE;
export const logicalPx = css => css / REF_SCALE;

// data: RGBA of dark text on a light opaque ground. Ink pixels become `rgb`, fully opaque; the rest clear.
// Returns how many pixels are ink.
export function oneBit(data, [r, g, b], cut = CUT) {
  let n = 0;
  for (let i = 0; i < data.length; i += 4) {
    const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    if (lum < cut) { data[i] = r; data[i + 1] = g; data[i + 2] = b; data[i + 3] = 255; n++; }
    else { data[i] = data[i + 1] = data[i + 2] = data[i + 3] = 0; }
  }
  return n;
}

// The overlay. The scene calls begin(), then say() for each line in logical (640x360) pixels, cover() where an
// opaque panel is drawn over earlier lines, and render(scale) once the frame is done.
export function createWords(canvas, doc = document) {
  const g = canvas.getContext('2d');
  const m = doc.createElement('canvas').getContext('2d', { willReadFrequently: true });
  const scratch = doc.createElement('canvas'), sg = scratch.getContext('2d', { willReadFrequently: true });
  let styles = null;
  // the two colours a thresholded line is drawn in: ink and paper (a level 2 night door, night.js: its night ink and cream)
  let inkRGB = [0, 0, 0], paperRGB = [255, 255, 255];
  const widths = new Map(), masks = new Map();
  const forget = () => { styles = null; widths.clear(); masks.clear(); lastKey = ''; };
  doc.fonts?.addEventListener?.('loadingdone', forget);

  function style(role) {
    if (!styles) {
      styles = {};
      for (const [r, sel] of Object.entries(ROLES)) {
        const el = doc.querySelector(sel), cs = el && getComputedStyle(el);
        const size = parseFloat(cs?.fontSize) || 16;
        const lh = parseFloat(cs?.lineHeight) || Math.round(size * 1.25);
        styles[r] = { size, lh, family: cs?.fontFamily || `"${SENTENCE_FONT}", Verdana, sans-serif`, weight: cs?.fontWeight || '400' };
      }
      // index.html swaps in onebit.css by script, so the first frames can still see the colour look's fonts:
      // until every role reads the sentence font, nothing measured or drawn is kept
      styles.ok = Object.keys(ROLES).every(r => styles[r].family.includes(SENTENCE_FONT));
    }
    return styles[role] ?? styles.chat;
  }
  const fontAt = (st, px) => `${st.weight} ${px}px ${st.family}`;

  // width in logical pixels (measured at the stylesheet size, which is the 2x screen size)
  // px: a size of its own in stylesheet px (screen px at 2x), for the first minute's one-line rule; otherwise the role's size
  function measure(s, role, px) {
    const k = role + '\u0000' + (px ?? '') + '\u0000' + s;
    if (!widths.has(k)) { const st = style(role); m.font = fontAt(st, px ?? st.size); widths.set(k, m.measureText(s).width / REF_SCALE); }
    return widths.get(k);
  }
  const lineHeight = (role, px) => (px ? Math.round(px * 1.25) : style(role).lh);
  const line = (role, px) => logicalPx(lineHeight(role, px));
  // the largest size (min..max) at which s fits room logical px on one line
  const fit = (s, role, room, min, max) => ruleFitPx(px => measure(s, role, px) * REF_SCALE, room * REF_SCALE, min, max);
  function wrap(s, role, max) {
    const out = []; let cur = '';
    for (const word of String(s).split(/\s+/).filter(Boolean)) {
      const t = cur ? cur + ' ' + word : word;
      if (measure(t, role) > max && cur) { out.push(cur); cur = word; } else cur = t;
    }
    if (cur) out.push(cur);
    return out;
  }

  let runs = [], lastKey = '';
  const begin = () => { runs = []; if (styles && !styles.ok) forget(); };
  // y is the top of the line box; v 0 = ink, 1 = paper; clip [x, y, w, h] in logical pixels
  function say(s, x, y, role, v = 0, align = 'left', clip = null, px = null) {
    s = String(s ?? ''); if (!s) return 0;
    const w = measure(s, role, px ?? undefined), left = align === 'right' ? x - w : align === 'center' ? x - w / 2 : x;
    runs.push({ s, x: left, y, w, h: line(role, px ?? undefined), role, v, clip, px });
    return w;
  }
  // an opaque panel drawn over the scene hides the lines under it
  function cover(x, y, w, h) { runs = runs.filter(r => r.x >= x + w || r.x + r.w <= x || r.y >= y + h || r.y + r.h <= y); }

  // one thresholded line at device scale k, cached
  function mask(r, k) {
    const key = `${k}\u0000${r.role}\u0000${r.px ?? ''}\u0000${r.v}\u0000${r.s}`;
    if (masks.has(key)) return masks.get(key);
    const st = style(r.role), px = (r.px ?? st.size) * k / REF_SCALE, lh = lineHeight(r.role, r.px ?? undefined) * k / REF_SCALE;
    sg.font = fontAt(st, px);
    const t = sg.measureText(r.s), asc = t.fontBoundingBoxAscent ?? px * 0.8, desc = t.fontBoundingBoxDescent ?? px * 0.25;
    const pad = 3, w = Math.ceil(t.width) + pad * 2, h = Math.ceil(asc + desc) + pad * 2;
    if (scratch.width < w) scratch.width = w;
    if (scratch.height < h) scratch.height = h;
    sg.fillStyle = '#fff'; sg.fillRect(0, 0, w, h);
    sg.font = fontAt(st, px); sg.textBaseline = 'alphabetic'; sg.fillStyle = '#000';
    sg.fillText(r.s, pad, pad + Math.round(asc));
    const img = sg.getImageData(0, 0, w, h);
    oneBit(img.data, r.v ? paperRGB : inkRGB, cutFor(st.weight));
    const c = doc.createElement('canvas'); c.width = w; c.height = h;
    c.getContext('2d').putImageData(img, 0, 0);
    // where the drawing sits against the line box: text centred in the box height
    const out = { c, dx: -pad, dy: Math.round((lh - (asc + desc)) / 2) - pad };
    if (masks.size > 600) masks.clear();
    masks.set(key, out);
    return out;
  }

  // draw the frame's lines; scale is the integer scene scale, dpr the device pixel ratio
  function render(scale, dpr = 1, W = 640, H = 360) {
    const k = scale * dpr, cw = Math.round(W * k), ch = Math.round(H * k);
    api.k = k;
    const key = `${k}|` + runs.map(r => `${r.s}\u0001${r.x}\u0001${r.y}\u0001${r.role}\u0001${r.px ?? ''}\u0001${r.v}\u0001${r.clip}`).join('\u0002');
    if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; lastKey = ''; }
    if (key === lastKey) return;
    lastKey = key;
    g.clearRect(0, 0, cw, ch);
    for (const r of runs) {
      const mk = mask(r, k);
      if (r.clip) { g.save(); g.beginPath(); g.rect(Math.round(r.clip[0] * k), Math.round(r.clip[1] * k), Math.round(r.clip[2] * k), Math.round(r.clip[3] * k)); g.clip(); }
      g.drawImage(mk.c, Math.round(r.x * k) + mk.dx, Math.round(r.y * k) + mk.dy);
      if (r.clip) g.restore();
    }
  }

  // the invert flash (call-lands.js): every line drawn this frame swaps ink for paper, as the raster under it does
  const flip = () => { for (const r of runs) r.v = r.v ? 0 : 1; };
  // '#rrggbb' ink and paper; the cached lines are drawn again in them
  const rgb = h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
  function colours(ink = '#000000', paper = '#ffffff') { inkRGB = rgb(ink); paperRGB = rgb(paper); masks.clear(); lastKey = ''; }
  const api = { begin, say, cover, measure, wrap, line, fit, render, flip, colours, runs: () => runs };
  // read-only handle for studio/tools/readable-shots.js, which finds a word's place on the canvas to crop it
  canvas.words = api;
  return api;
}

// Stand-in when there is no overlay (no DOM canvas): lines are measured roughly and not drawn.
export const noWords = {
  begin() {}, cover() {}, render() {}, runs: () => [],
  measure: s => String(s ?? '').length * 4.5, line: () => 11, fit: (s, role, room, min) => min,
  say(s, x, y, role, v, align) { return String(s ?? '').length * 4.5; },
  wrap(s, role, max) { const out = []; let cur = ''; for (const w of String(s).split(/\s+/).filter(Boolean)) { const t = cur ? cur + ' ' + w : w; if (t.length * 4.5 > max && cur) { out.push(cur); cur = w; } else cur = t; } if (cur) out.push(cur); return out; },
};
